import { Slider } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { SettingsContext } from "../../../../../settingsContext";
import RichText from "../../../../CmsComponents/RichText/RichText";
import Button, { IButton } from "../Button/Button";
import { BNBox } from "@bn-monorepo/js-packages";

interface Mark {
  value: number;
  label: string;
}

export interface ISlider {
  min: number;
  max: number;
  default: number;
  unitSymbol?: string;
  button?: IButton;
  queryParameterName?: string;
  step: number;
  ariaLabel: string;
  richText?: string;
  marks?: boolean | Mark[];
  title?: string;
  decimals?: number;
  hideCurrentValueLabel?: boolean | undefined;
  buttonClickHandler?: (newValue) => void;
  onChangeHandler?: (newValue) => void;
}

const StyledSlider = styled(Slider)(() => ({
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#003D50",
    border: "0.2rem solid currentColor",
    marginTop: 4,
    marginLeft: 0,
    "&:focus, &:hover, &.Mui-active": {
      boxShadow: "inherit",
    },
  },
  "& .MuiSlider-track": {
    height: 8,
    border: 0,
    backgroundColor: "transparent",
  },
  "& .MuiSlider-mark": {
    height: 8,
    width: 8,
    borderRadius: 4,
    transform: "translateX(-50%)",
    color: "#003D50",
  },
  "& .MuiSlider-markLabel": {
    lineHeight: 3,
  },
  "& .MuiSlider-markActive": {
    backgroundColor: "#003D50",
  },
  "& .MuiSlider-rail": {
    height: 14,
    borderRadius: 7,
    transform: "translateY(-0.3rem)",
    marginLeft: -7,
    width: "calc(100% + 1.4rem)",
  },
}));

const SliderComponent: ReactFCWithJsRequired<ISlider> = (props) => {
  const {
    ariaLabel,
    min,
    max,
    default: value,
    unitSymbol,
    button,
    queryParameterName,
    richText,
    step,
    marks,
    title,
    decimals,
    hideCurrentValueLabel,
    buttonClickHandler,
    onChangeHandler,
  } = props;

  const { locale } = useContext(SettingsContext);
  const [searchParams] = useSearchParams();
  const loanAmount = searchParams.get("loanamount");

  const getValidNumberValue = (value: number, min: number, max: number) => {
    if (isNaN(Number(value))) {
      return props.default;
    } else {
      return value >= min && value <= max ? value : value < min ? min : max;
    }
  };

  const initialValue = loanAmount
    ? getValidNumberValue(Number(loanAmount), min, max)
    : value > 0
      ? value
      : (min - max) / 2;

  const [sliderValue, setSliderValue] = useState(initialValue);
  useEffect(() => setSliderValue(sliderValue), [sliderValue]);

  const handleSliderChange = (_, newValue) => {
    setSliderValue(newValue);
    if (onChangeHandler) {
      onChangeHandler(newValue);
    }
  };
  const valuetext = (value) => {
    return `${value} ${unitSymbol}`;
  };
  const format = (value: number, maxDecimals: number = 0) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: maxDecimals,
    }).format(value);
  return (
    <div>
      {title ? (
        <BNBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "2rem",
          }}
        >
          <div>{title}</div>
          <div>
            {format(sliderValue, decimals)} {unitSymbol}
          </div>
        </BNBox>
      ) : hideCurrentValueLabel ? null : (
        <BNBox
          component={"p"}
          sx={{
            margin: 0,
            fontSize: "4rem",
            textAlign: "center",
            fontWeight: "normal",
            whiteSpace: "nowrap",
          }}
        >
          {format(sliderValue, decimals)} {unitSymbol}
        </BNBox>
      )}
      <StyledSlider
        aria-label={ariaLabel}
        getAriaValueText={valuetext}
        min={min}
        max={max}
        step={step}
        value={sliderValue}
        marks={marks}
        onChange={handleSliderChange}
        color="secondary"
      />
      {marks == null && (
        <BNBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <div>{format(min)}</div>
          <div>{format(max)}</div>
        </BNBox>
      )}
      {richText && <RichText html={richText} />}

      {button && (
        <Button
          {...button}
          href={buttonClickHandler ? "" : `${button.href}?${queryParameterName}=${sliderValue}`}
          onClick={buttonClickHandler ? () => buttonClickHandler(sliderValue) : undefined}
        />
      )}
    </div>
  );
};

export default SliderComponent;
